import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const InvoiceStatus = ({ status }) => {
  const getStatusColor = () => {
    switch (status.toLowerCase()) {
      case 'paid':
        return THEME.success;
      case 'overdue':
        return THEME.error;
      case 'sent':
        return THEME.warning;
      default:
        return THEME.subtext;
    }
  };

  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{
        backgroundColor: `${getStatusColor()}15`,
        color: getStatusColor(),
      }}
    >
      {status}
    </span>
  );
};

const PaymentModal = ({ invoice, onClose, onSave }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: invoice.total_amount - (invoice.paid_amount || 0),
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer',
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Add payment record
      const { error: paymentError } = await supabase
        .from('payments')
        .insert([{
          invoice_id: invoice.invoice_id,
          user_id: user.id,
          ...formData,
        }]);

      if (paymentError) throw paymentError;

      // Update invoice status if fully paid
      const newPaidAmount = (invoice.paid_amount || 0) + parseFloat(formData.amount);
      const newStatus = newPaidAmount >= invoice.total_amount ? 'Paid' : 'Sent';

      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({ 
          status: newStatus,
          paid_amount: newPaidAmount,
        })
        .eq('invoice_id', invoice.invoice_id);

      if (invoiceError) throw invoiceError;

      onSave();
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6" style={{ boxShadow: shadows.xl }}>
        <h2 className="text-xl font-bold mb-4" style={{ color: THEME.text }}>
          Record Payment
        </h2>

        {error && (
          <div 
            className="rounded-lg p-4 mb-4 text-sm"
            style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Amount
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
              min="0.01"
              max={invoice.total_amount - (invoice.paid_amount || 0)}
              step="0.01"
            />
            <p className="mt-1 text-sm" style={{ color: THEME.subtext }}>
              Remaining: ${(invoice.total_amount - (invoice.paid_amount || 0)).toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Payment Date
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Payment Method
            </label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Check">Check</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={2}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              placeholder="Add any payment notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: THEME.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              style={{ 
                backgroundColor: THEME.primary,
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Invoices() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Search and filter states
  const [searchField, setSearchField] = useState('invoice_number');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [selectedStatus, setSelectedStatus] = useState('');

  const searchFields = [
    { value: 'invoice_number', label: 'Invoice Number' },
    { value: 'client_name', label: 'Client Name' },
    { value: 'status', label: 'Status' },
    { value: 'invoice_date', label: 'Invoice Date' },
    { value: 'due_date', label: 'Due Date' },
    { value: 'total_amount', label: 'Amount' },
  ];

  const { logEvent } = useAnalytics();

  useEffect(() => {
    if (user) {
      fetchInvoices();
    }
  }, [user]);

  useEffect(() => {
    filterInvoices();
  }, [invoices, searchField, searchQuery, dateRange, selectedStatus]);

  const filterInvoices = () => {
    let filtered = [...invoices];

    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(invoice => {
        if (searchField === 'invoice_number') {
          return invoice.invoice_number.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'client_name') {
          return invoice.clients?.client_name.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'status') {
          return invoice.status.toLowerCase().includes(searchQuery.toLowerCase());
        } else if (searchField === 'total_amount') {
          return invoice.total_amount.toString().includes(searchQuery);
        }
        return true;
      });
    }

    // Apply date range filter
    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(invoice => {
        const invoiceDate = new Date(searchField === 'due_date' ? invoice.due_date : invoice.invoice_date);
        
        if (dateRange.startDate && dateRange.endDate) {
          return invoiceDate >= new Date(dateRange.startDate) && invoiceDate <= new Date(dateRange.endDate);
        } else if (dateRange.startDate) {
          return invoiceDate >= new Date(dateRange.startDate);
        } else if (dateRange.endDate) {
          return invoiceDate <= new Date(dateRange.endDate);
        }
        return true;
      });
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(invoice => invoice.status === selectedStatus);
    }

    setFilteredInvoices(filtered);
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
  };

  const handleSearchQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    logEvent('Invoices', 'Status Change', `Status: ${e.target.value}`);
  };

  const clearFilters = () => {
    setSearchField('invoice_number');
    setSearchQuery('');
    setDateRange({ startDate: '', endDate: '' });
    setSelectedStatus('');
    setShowFilters(false);
  };

  const fetchInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          clients (
            client_id,
            client_name,
            client_email
          ),
          payments (
            amount,
            payment_date,
            payment_method
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('invoice_id', invoiceId);
      logEvent('Invoices', 'Delete Invoice', `Invoice ID: ${invoiceId}`);
      if (error) throw error;
      await fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setError('Failed to delete invoice');
    }
  };

  const handlePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setSelectedInvoice(null);
    setShowPaymentModal(false);
  };

  const handleSavePayment = async () => {
    logEvent('Invoices', 'Record Payment', `Invoice ID: ${selectedInvoice.invoice_id}`);
    await fetchInvoices();
    handleClosePaymentModal();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Invoices</h1>
          <p style={{ color: THEME.subtext }}>Manage your invoices and payments</p>
        </div>
        <Link
          to="/invoices/new"
          className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
          style={{ 
            background: THEME.primary,
            boxShadow: shadows.md,
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Invoice
        </Link>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 flex items-center space-x-2">
            <select
              value={searchField}
              onChange={handleSearchFieldChange}
              className="rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
            >
              {searchFields.map(field => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </select>
            
            {searchField === 'invoice_date' || searchField === 'due_date' ? (
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateRangeChange}
                  className="flex-1 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder="Start Date"
                />
                <span style={{ color: THEME.text }}>to</span>
                <input
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateRangeChange}
                  className="flex-1 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder="End Date"
                />
              </div>
            ) : (
              <div className="flex-1 relative">
                <input
                  type={searchField === 'total_amount' ? 'number' : 'text'}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  className="w-full rounded-lg pl-10 border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  placeholder={`Search by ${searchFields.find(f => f.value === searchField)?.label}`}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 rounded-lg transition-colors duration-200"
            style={{ 
              backgroundColor: showFilters ? `${THEME.primary}15` : 'transparent',
              color: showFilters ? THEME.primary : THEME.text 
            }}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>

          {(searchQuery || dateRange.startDate || dateRange.endDate || selectedStatus) && (
            <button
              onClick={clearFilters}
              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors duration-200"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {showFilters && (
          <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: `${THEME.primary}08` }}>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={handleStatusChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
        >
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl" style={{ boxShadow: shadows.md }}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" style={{ color: THEME.primary }} />
                      <span style={{ color: THEME.text }}>#{invoice.invoice_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {invoice.clients?.client_name}
                    </div>
                    <div className="text-sm" style={{ color: THEME.subtext }}>
                      {invoice.clients?.client_email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: THEME.text }}>
                      ${invoice.total_amount?.toFixed(2)}
                    </div>
                    {invoice.paid_amount > 0 && (
                      <div className="text-sm" style={{ color: THEME.success }}>
                        Paid: ${invoice.paid_amount.toFixed(2)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <InvoiceStatus status={invoice.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: THEME.text }}>
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      {invoice.status !== 'Paid' && (
                        <button
                          onClick={() => handlePayment(invoice)}
                          className="text-gray-400 hover:text-gray-500"
                          title="Record Payment"
                        >
                          <CreditCardIcon className="h-5 w-5" />
                        </button>
                      )}
                      <Link
                        to={`/invoices/${invoice.invoice_id}`}
                        className="text-gray-400 hover:text-gray-500"
                        title="Edit Invoice"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(invoice.invoice_id)}
                        className="text-gray-400 hover:text-red-500"
                        title="Delete Invoice"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      <Link
                        to={`/invoices/${invoice.invoice_id}`}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center" style={{ color: THEME.subtext }}>
                    {invoices.length === 0 ? 
                      'No invoices found. Create your first invoice to get started!' :
                      'No invoices match your search criteria.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={handleClosePaymentModal}
          onSave={handleSavePayment}
        />
      )}
    </div>
  );
} 